import { getIdToken } from "src/domains/shared/utils/amplifyCompat";
import { backendBaseUrl } from "src/domains/shared/api/calculation/backendBaseUrl";
import {
  EXCLUSIVE_LOGIN_METHOD,
  EXCLUSIVE_LOGIN_PATH,
  classifyClaimResponse,
  classifyStatusResponse,
  type LoginClaimResult,
  type LoginStatusResult,
} from "./exclusiveLogin.model";

/**
 * 해제 상한. 해제는 best-effort 라서 상한 안에 끝나지 않거나 실패해도 로그아웃을 막지 않는다
 * (웹 스펙 「명시적 로그아웃은 전파·해제·로컬 로그아웃 순서로 진행한다」).
 */
const RELEASE_TIMEOUT_MS = 3_000;

/** 점유·상태·해제가 함께 쓰는 main-api URL. */
export function exclusiveLoginUrl(): string {
  return `${backendBaseUrl()}/${EXCLUSIVE_LOGIN_PATH}`;
}

/**
 * 판정에 쓰는 본문은 실패 응답의 것뿐이다(`401` 의 `code`). 성공 본문은 읽지 않는다.
 * 본문을 읽지 못하면 `code` 가 없는 것으로 본다.
 */
async function readFailureBody(response: Response): Promise<string | null> {
  if (response.ok) return null;
  try {
    return await response.text();
  } catch {
    return null;
  }
}

/**
 * 로그인 자리를 점유한다(`PUT`, 본문 `{"force": boolean}`). throw 하지 않는다 — 네트워크 실패를 포함한
 * 모든 실패는 판정 결과로 돌려준다.
 *
 * `idToken` 을 주면 그 토큰을 `Bearer` 로 직접 싣는다. SSO 콜백은 Amplify 메모리 세션이 주입 전 것일 수
 * 있어 받은 토큰으로 점유한다. 주지 않으면 현재 세션의 토큰(`getIdToken`)을 쓴다.
 *
 * 점유 대기 중(이 브라우저의 어느 탭에서든 점유가 진행 중)에 fetch 관문이 네트워크 없이 거절하는 것은 점유 `PUT`
 * 외의 요청이다 — 이 요청은 그 거절에서 빠진다(`installExclusiveLoginFetchGate`).
 */
export async function claimLogin({
  force,
  idToken,
}: {
  force: boolean;
  idToken?: string;
}): Promise<LoginClaimResult> {
  try {
    const authorization = idToken ? `Bearer ${idToken}` : await getIdToken();
    const response = await fetch(exclusiveLoginUrl(), {
      method: EXCLUSIVE_LOGIN_METHOD.claim,
      headers: { "Content-Type": "application/json", Authorization: authorization },
      body: JSON.stringify({ force }),
    });
    return classifyClaimResponse(response.status, await readFailureBody(response));
  } catch {
    return "unavailable";
  }
}

/**
 * 열린 화면의 로그인 상태를 확인한다(`GET`). throw 하지 않는다 — 네트워크 실패는 `unknown` 이다.
 * 응답 캐시를 쓰지 않는다. 이 요청이 서버에 닿아야 "보이는 화면이 사용 중" 이 기록된다.
 *
 * 점유 대기 중(이 브라우저의 어느 탭에서든 점유가 진행 중)에는 fetch 관문이 이 요청을 네트워크 없이 거절한다 —
 * 서버에 닿지 않고 결과는 `unknown` 이다. 단 이 탭이 밀려남 처리 중이면 관문은 거절보다 앞서 합성 밀려남 `401` 로
 * 답하고 결과는 `superseded` 다(`installExclusiveLoginFetchGate`).
 */
export async function checkLoginStatus(): Promise<LoginStatusResult> {
  try {
    const response = await fetch(exclusiveLoginUrl(), {
      method: EXCLUSIVE_LOGIN_METHOD.status,
      headers: { Authorization: await getIdToken() },
      cache: "no-store",
    });
    return classifyStatusResponse(response.status, await readFailureBody(response));
  } catch {
    return "unknown";
  }
}

/**
 * 로그인 자리를 해제한다(`DELETE`). 토큰이 남아 있는 동안(로컬 로그아웃 전에) 불러야 한다.
 * best-effort 다: 응답을 판정하지 않고, 실패는 삼키며, 상한(기본 3초)이 지나면 요청을 끊고 끝난다.
 *
 * 점유 대기 중(이 브라우저의 어느 탭에서든 점유가 진행 중)에는 fetch 관문이 이 요청을 네트워크 없이 막는다 —
 * 해제는 서버에 닿지 않고, 상한을 기다리지 않고 바로 끝난다(`installExclusiveLoginFetchGate`).
 */
export async function releaseLogin({
  timeoutMs = RELEASE_TIMEOUT_MS,
}: { timeoutMs?: number } = {}): Promise<void> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve();
    }, timeoutMs);
  });
  const release = (async () => {
    const authorization = await getIdToken();
    await fetch(exclusiveLoginUrl(), {
      method: EXCLUSIVE_LOGIN_METHOD.release,
      headers: { Authorization: authorization },
      signal: controller.signal,
    });
  })().catch(() => undefined);

  try {
    await Promise.race([release, deadline]);
  } finally {
    clearTimeout(timer);
  }
}
